import { Sparkles, Banknote, Calendar, Repeat, Megaphone, Trophy } from 'lucide-react'
import { Button, Card, CardTitle, CardDescription, Badge } from '@/components/ui'
import { useGenerateValuation } from '@/hooks/useFunctions'
import { VaultLinkBar } from '@/components/VaultLinkBar'
import { defaultValuationGuess } from '@/lib/scoring'
import { formatCurrency, formatRelativeDate, cn } from '@/lib/utils'
import type { AppRecord, ValuationBand } from '@/lib/types'

export function ValuationPanel({ app }: { app: AppRecord }) {
  const gen = useGenerateValuation()
  const v = app.valuation

  return (
    <Card className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="eyebrow mb-1">Valuation</div>
          <CardTitle>What this app is worth</CardTitle>
          <CardDescription className="mt-1">
            {v
              ? <>Generated <span className="text-gold-soft">{formatRelativeDate(v.generated_at)}</span> · model <span className="font-mono text-bronze">{v.model}</span></>
              : 'No AI valuation yet — heuristics shown below. Generate to refine.'}
          </CardDescription>
        </div>
        <Button onClick={() => gen.mutate(app.slug)} loading={gen.isPending} variant="gold-outline" size="sm">
          <Sparkles size={12} />
          {v ? 'Regenerate' : 'Generate'}
        </Button>
      </div>

      <div className="divider-editorial" />

      {v ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ValuationCard icon={<Trophy size={14} />} label="Acquisition (full sale)" band={v.acquisition} tone="gold" prominent />
          <ValuationCard icon={<Banknote size={14} />} label="One-time buildout" band={v.one_time_buildout} tone="bronze" />
          <ValuationCard icon={<Repeat size={14} />} label="Monthly subscription / seat" band={v.monthly_subscription} tone="info" suffix="/mo" />
          <ValuationCard icon={<Calendar size={14} />} label="Annual license" band={v.annual_license} tone="success" suffix="/yr" />
          <ValuationCard icon={<Megaphone size={14} />} label="Pitch / engagement value" band={v.pitch_value} tone="cream" />
        </div>
      ) : (
        <HeuristicGuess app={app} />
      )}

      {v?.acquisition.comparables && v.acquisition.comparables.length > 0 && (
        <div>
          <div className="eyebrow mb-2">Comparables</div>
          <div className="flex flex-wrap gap-1.5">
            {v.acquisition.comparables.map((c: string) => <Badge key={c} tone="cream">{c}</Badge>)}
          </div>
        </div>
      )}

      {v?.acquisition.rationale && (
        <div className="bg-navy/40 border border-bronze/15 rounded-md p-4">
          <div className="eyebrow mb-2">Rationale</div>
          <p className="font-body text-sm text-cream/75 leading-relaxed">{v.acquisition.rationale}</p>
        </div>
      )}

      {/* Only surface the valuation doc / vault actions once a valuation exists. */}
      {v && <VaultLinkBar app={app} kind="valuation" />}
    </Card>
  )
}

function ValuationCard({ icon, label, band, tone, suffix, prominent }: {
  icon: React.ReactNode
  label: string
  band: ValuationBand
  tone: 'gold' | 'bronze' | 'success' | 'info' | 'cream'
  suffix?: string
  prominent?: boolean
}) {
  const t = {
    gold: { txt: 'text-gold-soft', border: 'border-gold/40', mid: 'text-gold-soft' },
    bronze: { txt: 'text-bronze', border: 'border-bronze/40', mid: 'text-bronze' },
    success: { txt: 'text-success', border: 'border-success/40', mid: 'text-success' },
    info: { txt: 'text-info', border: 'border-info/40', mid: 'text-info' },
    cream: { txt: 'text-cream', border: 'border-cream/30', mid: 'text-cream' },
  }[tone]
  return (
    <div className={cn('rounded-lg border bg-navy/40 p-4', t.border, prominent && 'border-2 col-span-full')}>
      <div className={cn('flex items-center gap-2 mb-2', t.txt)}>
        {icon}
        <span className="eyebrow">{label}</span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div>
          <div className={cn('font-display italic font-light leading-none', prominent ? 'text-5xl' : 'text-3xl', t.mid)}>
            {formatCurrency(band.midpoint, { compact: true })}{suffix}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/45 mt-2">
            Range: {formatCurrency(band.low, { compact: true })} – {formatCurrency(band.high, { compact: true })}{suffix}
          </div>
        </div>
        {band.multiplier && (
          <div className="font-mono text-xs text-bronze">
            ×{band.multiplier.toFixed(2)}
          </div>
        )}
      </div>
    </div>
  )
}

function HeuristicGuess({ app }: { app: AppRecord }) {
  const g = defaultValuationGuess(app)
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="bg-navy/40 border border-bronze/15 rounded-lg p-4">
        <div className="eyebrow mb-2">Acquisition (heuristic)</div>
        <div className="font-display italic text-3xl text-gold-soft font-light leading-none mb-1">
          {formatCurrency(g.acquisition_mid, { compact: true })}
        </div>
        <div className="font-mono text-[10px] text-cream/45">
          {formatCurrency(g.acquisition_low, { compact: true })} – {formatCurrency(g.acquisition_high, { compact: true })}
        </div>
      </div>
      <div className="bg-navy/40 border border-bronze/15 rounded-lg p-4">
        <div className="eyebrow mb-2">One-time buildout</div>
        <div className="font-display italic text-3xl text-bronze font-light leading-none mb-1">
          {formatCurrency(g.one_time_mid, { compact: true })}
        </div>
        <div className="font-mono text-[10px] text-cream/45">
          {formatCurrency(g.one_time_low, { compact: true })} – {formatCurrency(g.one_time_high, { compact: true })}
        </div>
      </div>
      <div className="bg-navy/40 border border-bronze/15 rounded-lg p-4">
        <div className="eyebrow mb-2">Subscription / mo</div>
        <div className="font-display italic text-3xl text-info font-light leading-none mb-1">
          {formatCurrency(g.monthly_mid, { compact: true })}
        </div>
        <div className="font-mono text-[10px] text-cream/45">
          {formatCurrency(g.monthly_low, { compact: true })} – {formatCurrency(g.monthly_high, { compact: true })}
        </div>
      </div>
    </div>
  )
}
